// AppCore.js

// ⬇️ Copy everything from your current App.js (excluding import/export) into here.
// Your states, useEffects, chat/calendar/dashboard/rendering logic — all stays intact.

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";
import LandingPage from './LandingPage';
import { useNavigate } from "react-router-dom";

function AppCore(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [activeProject, setActiveProject] = useState(null); // Start as null
  const [chatHistory, setChatHistory] = useState({});
  const [message, setMessage] = useState("");
  //const [calendarEvents, setCalendarEvents] = useState({});
  //const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  //const [currentMonth, setCurrentMonth] = useState(new Date());
  //const [eventDetails, setEventDetails] = useState({ title: "", time: "", link: "", context: "" });
  const [chatFilter, setChatFilter] = useState(""); // For Chat Tab
  const [dashboardFilters, setDashboardFilters] = useState({ source_type: "", knowledge_type: "" });
  const [insights, setInsights] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [projects, setProjects] = useState([]);
  const [deepMode, setDeepMode] = useState(false); // 🔍 OCR toggle
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showMsModal, setShowMsModal] = useState(false);
  const { token, refreshToken, handleLogout } = props;
  const [userProfile, setUserProfile] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE;
  const [newProject, setNewProject] = useState({
    name: "",
    number: "",
    status: "",
    manager: "",
    budget: "",
    deadline: "",
    project_type: ""
  });
  ;
  const navigate = useNavigate();

  const handleMicrosoftLogin = () => {
    setShowMsModal(true);
  };  

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");

    if (status === "connected") {
      alert("✅ Microsoft account connected successfully!");
    } else if (status === "admin-approved") {
      alert("✅ Admin permissions granted. You're all set!");
    }
}, []);

  //create dynamic dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      if (!token) return;
  
      try {
        const response = await fetch(`${API_BASE}/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        if (!response.ok) throw new Error("Failed to fetch projects");
        const projectData = await response.json();
  
        setProjects(projectData);
  
        // ✅ Ensure the first available project is selected, but only if no project is set
        if (!activeProject && projectData.length > 0) {
          console.log("🔀 Auto-selecting first project:", projectData[0]); // Debugging
          setActiveProject(projectData[0]); // ✅ Correct way to initialize
        }
      } catch (error) {
        console.error("❌ Error fetching projects:", error);
      }
    };
  
    fetchProjects();
  }, [token]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch user profile");
        const user = await res.json();
        setUserProfile(user);
        setNewProject((prev) => ({ ...prev, client_id: user.client_id }));
      } catch (err) {
        console.error("❌ Failed to fetch client ID from /auth/me:", err);
      }
    };
  
    fetchUserProfile();
  }, [token]);

  // Handle Chat Message Send
  const handleSendMessage = async () => {
    if (!message.trim() || !token || !activeProject) {
      console.error("❌ No active project selected.");
      return;
    }
  
    // ✅ Add user message immediately to chat UI
    setChatHistory((prev) => ({
      ...prev,
      [activeProject.id]: [
        ...(prev[activeProject.id] || []),
        { sender: "User", text: message },
      ],
    }));
  
    const requestBody = {
      project_number: activeProject.number,
      message,
    };
  
    console.log("📨 Sending chat request:", requestBody);
  
    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Chat request failed:", errorText);
        throw new Error("Failed to fetch response from Auden.");
      }
  
      const data = await response.json();
      console.log("✅ Chat request success. Waiting 1s before starting stream...");
  
      setTimeout(() => {
        startChatStream(data.user_id, data.project_id);
      }, 1000);
  
    } catch (error) {
      console.error("Chat error:", error);
    } finally {
      setMessage(""); // ✅ Clear input field after sending
    }
  };
  
  
  const startChatStream = (userId, projectId) => {
    if (!userId || !projectId) return;

    const eventSource = new EventSource(
      `${API_BASE}/chat/stream?user_id=${userId}&project_id=${projectId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const newChunk = data.message;
    
        setChatHistory((prev) => {
          const current = prev[projectId] || [];
          const lastMsg = current[current.length - 1];
    
          // If last message is Auden's, append to it
          if (lastMsg && lastMsg.sender === "Auden") {
            const updated = [...current];
            updated[updated.length - 1] = {
              ...lastMsg,
              text: lastMsg.text + newChunk,
            };
            return {
              ...prev,
              [projectId]: updated,
            };
          }
    
          // Otherwise, push new Auden message
          return {
            ...prev,
            [projectId]: [...current, { sender: "Auden", text: newChunk }],
          };
        });
      } catch (error) {
        console.error("Error parsing streamed message:", error);
      }
    };    

    eventSource.onerror = (error) => {
      console.error("Error with EventSource:", error);
      eventSource.close();
    };

    return () => eventSource.close(); // ✅ Cleanup on unmount
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // Handle File upload
  const handleFileUpload = async () => {
    if (!selectedFile || !activeProject) {
      console.error("❌ No active project selected for file upload.");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("project_id", activeProject.id);
    formData.append("deep_mode", deepMode);
  
    // 🔄 Stream status updates from Auden
    const statusStream = new EventSource(
      `${API_BASE}/files/stream?project_id=${activeProject.id}`
    );
  
    statusStream.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const status = data.message;
  
        setChatHistory((prev) => ({
          ...prev,
          [activeProject.id]: [
            ...(prev[activeProject.id] || []),
            { sender: "Auden", text: status },
          ],
        }));
  
        if (status.includes("✅ File processing complete")) {
          statusStream.close();
        }
      } catch (err) {
        console.error("Status stream parse error:", err);
      }
    };
  
    statusStream.onerror = (error) => {
      console.error("Status stream error:", error);
      statusStream.close();
    };
  
    try {
      const response = await axios.post(`${API_BASE}/files/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });
  
      console.log("File uploaded:", response.data);
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };
  
  // Filtered Messages for Chat
  const filteredMessages =
    chatHistory[activeProject?.id || ""]?.filter((msg) =>
      chatFilter.trim() !== "" ? msg.text.toLowerCase().includes(chatFilter.toLowerCase()) : true
    ) || [];

  useEffect(() => {
    const chatWindow = document.querySelector(".Chat-window");
  
    if (chatWindow && filteredMessages.length > 0) {
      chatWindow.scrollTop = chatWindow.scrollHeight;
    }
  }, [filteredMessages.length]);

  // Handle Dashboard Filters
  const handleDashboardFilterChange = (e) => {
    const { name, value } = e.target;
    setDashboardFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filtered Insights for Dashboard
  useEffect(() => {
    const fetchInsights = async () => {
      if (!activeProject) return;
      try {
        const params = new URLSearchParams();
        params.append("project_id", activeProject.id);
        if (dashboardFilters.source_type) params.append("source_type", dashboardFilters.source_type);
        if (dashboardFilters.knowledge_type) params.append("knowledge_type", dashboardFilters.knowledge_type);
  
        const res = await fetch(`${API_BASE}/dashboard/insights?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        if (!res.ok) throw new Error("Failed to fetch insights");
        const data = await res.json();
        setInsights(data);
      } catch (err) {
        console.error("Insight fetch error:", err);
      }
    };
  
    fetchInsights();
  }, [activeProject, dashboardFilters, token]);

  {/*
  // Handle Calendar Navigation
  const handleDateChange = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate.toLocaleDateString("en-CA")); // Use 'en-CA' to get YYYY-MM-DD format
  };

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleSpecificMonthChange = (e) => {
    const [year, month] = e.target.value.split("-");
    const newMonth = new Date(year, parseInt(month) - 1);
    setCurrentMonth(newMonth);
  };
  
  // Generate Calendar Days
  const generateCalendarDays = () => {
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    return [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  };

  // Handle Calendar Event Input
  const handleEventChange = (e) => {
    const { name, value } = e.target;
    setEventDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Add Calendar Event
  const handleAddEvent = () => {
    if (!eventDetails.title || !eventDetails.time) return; // Basic validation
    setCalendarEvents((prev) => ({
      ...prev,
      [selectedDate]: [
        ...(prev[selectedDate] || []),
        {
          ...eventDetails,
          project: activeProject.name,
        },
      ],
    }));
    setEventDetails({
      title: "",
      time: "",
      link: "",
      context: "",
    });
  };
  */}

  console.log("🧪 chatHistory:", chatHistory);
  console.log("🧪 activeProject ID:", activeProject?.id);
  console.log("🧪 filteredMessages:", filteredMessages);

  return (
    <div className="App">
        <div>
          <header className="App-header">
            <div className="Header-container">
              <div className="Header-left">
                <div className="Project-info">
                  <p>Active Project</p>

   
                  <div className="Project-select-row">
                    <select
                      className="Project-selector"
                      value={activeProject?.number || ""}
                      onChange={(e) => {
                        const selectedProject = projects.find((proj) => proj.number === e.target.value);
                        setActiveProject(selectedProject || null);
                      }}
                    >
                      {projects.length > 0 ? (
                        projects.map((proj) => (
                          <option key={proj.number} value={proj.number}>
                            {proj.name} | {proj.number}
                          </option>
                        ))
                      ) : (
                        <option value="">No projects available</option>
                      )}
                    </select>

                    <button className="Add-project-button" onClick={() => setShowProjectModal(true)}>
                      +
                    </button>
                  </div>

                  {showProjectModal && (
                    <div className="Modal-backdrop">
                      <div className="Modal">
                        <h3>Create New Project</h3>
                        <input
                          type="text"
                          placeholder="Project Name"
                          value={newProject.name}
                          onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Project Number"
                          value={newProject.number}
                          onChange={(e) => setNewProject({ ...newProject, number: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Project Manager (Optional)"
                          value={newProject.manager || ""}
                          onChange={(e) => setNewProject({ ...newProject, manager: e.target.value })}
                       />
                        <input
                          type="text"
                          placeholder="Budget (Optional)"
                          value={newProject.budget || ""}
                          onChange={(e) => setNewProject({ ...newProject, budget: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Deadline (Optional)"
                          value={newProject.deadline || ""}
                          onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })}
                        />         
                        <select
                          value={newProject.project_type}
                          onChange={(e) =>
                            setNewProject({ ...newProject, project_type: e.target.value })
                          }
                        >
                          <option value="">Select Project Type</option>
                          <option value="construction">Construction</option>
                          <option value="software">Software</option>
                        </select>                                  
                        <select
                          value={newProject.status}
                          onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}
                        >
                          <option value="">Select Project Status</option>
                          <option value="active">Active</option>
                          <option value="planning">Planning</option>
                          <option value="completed">Completed</option>
                        </select>
                        <div className="Modal-buttons">
                          <button onClick={() => setShowProjectModal(false)}>Cancel</button>
                          <button
                            onClick={async () => {
                              try {
                               const payload = {
                                 name: newProject.name,
                                 number: newProject.number,
                                 status: newProject.status,
                                 client_id: newProject.client_id,
                                 project_type: newProject.project_type,
                                 project_metadata: {
                                   manager: newProject.manager,
                                   budget: newProject.budget,
                                   deadline: newProject.deadline
                                 }
                               };
                                console.log("🚧 Payload being sent:", payload);

                                const res = await fetch(`${API_BASE}/projects/create`, {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                  },
                                  body: JSON.stringify(payload),
                                });
                                if (!res.ok) throw new Error("Failed to create project");
                                const created = await res.json();
                                setProjects([...projects, created]);
                                setActiveProject(created);
                                setShowProjectModal(false);
                               setNewProject({
                                 name: "",
                                 number: "",
                                 status: "",
                                 manager: "",
                                 budget: "",
                                 project_type: "",
                                 deadline: ""
                               });                              } catch (err) {
                                alert("Error creating project: " + err.message);
                              }
                            }}
                          >
                            Create
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <nav className="Navigation-tabs">
                    <button
                      className={`Tab-button ${activeTab === "Dashboard" ? "active" : ""}`}
                      onClick={() => setActiveTab("Dashboard")}
                    >
                      Dashboard
                    </button>
                    <button
                      className={`Tab-button ${activeTab === "Chat" ? "active" : ""}`}
                      onClick={() => setActiveTab("Chat")}
                    >
                      Chat
                    </button>
       {/*          <button
                      className={`Tab-button ${activeTab === "Calendar" ? "active" : ""}`}
                      onClick={() => setActiveTab("Calendar")}
                    >
                      Calendar
                    </button>   */}
                  </nav>
                </div>

              </div>
              <div className="Header-center">
                <h1 className="App-title">Auden AI</h1>
                <div className="App-slogan">Your Project's Hippocampus</div>
              </div>
              <div className="Header-right">

                <button className="Logout-button" onClick={handleLogout}>Logout</button>
              </div>
            </div>
          </header>

          <main className="App-main">
            {activeTab === "Chat" && (
              <div className="Chat-section">
                <div className="Metadata-display">
                  <div className="Search-bar">
                    <input
                      type="text"
                      placeholder="Search chat history..."
                      value={chatFilter}
                      onChange={(e) => setChatFilter(e.target.value || "")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") console.log("Search triggered:", chatFilter);
                      }}
                    />
                  </div>
                </div>
                <div className="Chat-window">
                  {filteredMessages.map((msg, index) => (
                    <div key={index} className={`Chat-message ${msg.sender}`}>
                      <strong>{msg.sender}:</strong> {msg.text}
                    </div>
                  ))}
                </div>
                <div className="Chat-input">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="Upload-button">
                    📎
                  </label>
                                 
                  {selectedFile && (
                    <button className="Upload-confirm" onClick={handleFileUpload}>
                      Upload {uploadProgress > 0 && `(${uploadProgress}%)`}
                    </button>
                  )}

                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <button onClick={handleSendMessage}>Send</button>
                </div>              
                </div>
            )}

            {activeTab === "Dashboard" && (
              <div className="Dashboard-container">
                {activeProject ? (
                  <>
                    <h2>Dashboard for {activeProject.name}</h2>
                    
                    <button onClick={handleMicrosoftLogin} className="Ms-login-button">
                      Connect Microsoft Account
                    </button>                    

                    <div className="Filter-bar">
                      <select
                        name="source_type"
                        value={dashboardFilters.source_type}
                        onChange={handleDashboardFilterChange}
                      >
                        <option value="">All Source Types</option>
                        <option value="chat">Chat</option>
                        <option value="file">File</option>
                      </select>
                      <select
                        name="knowledge_type"
                        value={dashboardFilters.knowledge_type}
                        onChange={handleDashboardFilterChange}
                      >
                        <option value="">All Knowledge Types</option>
                        <option value="project">Project</option>
                        <option value="institutional">Institutional</option>
                      </select>
                    </div>
                    <ul>
                      <div className="Insight-grid">
                        {insights.map((insight) => (
                          <div key={insight.id} className="Insight-card">
                            <div className="Insight-content">{insight.content.slice(0, 200)}...</div>
                            <div className="Insight-meta">
                              <span>{insight.knowledge_type}</span>
                              <span>{insight.source_type === "file" ? `File: ${insight.filename}` : "Chat"}</span>
                              <span>{new Date(insight.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="Insight-tags">
                              {insight.keywords?.slice(0, 4).map((kw, i) => (
                                <span key={i} className="Tag">{kw}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                    </ul>
                  </>
                ) : (
                  <h2>Select a project to view the dashboard</h2> // ✅ Prevents crash when no project is selected
                )}
              </div>
            )}

{/*
            {activeTab === "Calendar" && (
              <div className="Calendar-section">
                <h2>
                  {currentMonth.toLocaleString("default", { month: "long" })} {currentMonth.getFullYear()}
                </h2>
                <div className="Calendar-controls">
                  <button onClick={() => handleMonthChange(-1)}>←</button>
                  <select
                    value={`${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`}
                    onChange={handleSpecificMonthChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") console.log("Month selected:", currentMonth);
                    }}
                  >
                    {Array.from({ length: 12 }).map((_, i) => {
                      const date = new Date(currentMonth.getFullYear(), i);
                      return (
                        <option
                          key={i}
                          value={`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`}
                        >
                          {date.toLocaleString("default", { month: "long", year: "numeric" })}
                        </option>
                      );
                    })}
                  </select>
                  <button onClick={() => handleMonthChange(1)}>→</button>
                </div>
                <div className="Calendar-grid">
                  {generateCalendarDays().map((day, index) => (
                    <button
                      key={index}
                      className={`Calendar-day ${
                        day && selectedDate.endsWith(`-${String(day).padStart(2, "0")}`) ? "selected" : ""
                      }`}
                      onClick={() => day && handleDateChange(day)}
                      disabled={!day}
                    >
                      {day || ""}
                    </button>
                  ))}
                </div>
                <div className="Event-form">
                  <h3>Add Event for {selectedDate}</h3>
                  <input
                    type="text"
                    name="title"
                    placeholder="Event Title"
                    value={eventDetails.title}
                    onChange={handleEventChange}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEvent()}
                  />
                  <input
                    type="time"
                    name="time"
                    placeholder="Event Time"
                    value={eventDetails.time}
                    onChange={handleEventChange}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEvent()}
                  />
                  <input
                    type="text"
                    name="link"
                    placeholder="Meeting Link"
                    value={eventDetails.link}
                    onChange={handleEventChange}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEvent()}
                  />
                  <textarea
                    name="context"
                    placeholder="Event Context/Goals"
                    value={eventDetails.context}
                    onChange={handleEventChange}
                    onKeyDown={(e) => e.key === "Enter" && handleAddEvent()}
                  />
                  <button onClick={handleAddEvent}>Add Event</button>
                </div>
                <div className="Event-list">
                  <h3>Scheduled Events for {selectedDate}</h3>
                  {calendarEvents[selectedDate] && calendarEvents[selectedDate].length > 0 ? (
                    calendarEvents[selectedDate].map((event, index) => (
                      <div key={index} className="Event-item">
                        <strong>{event.title}</strong> at {event.time}
                        <p>{event.context}</p>
                        {event.link && (
                          <small>
                            <a href={event.link} target="_blank" rel="noopener noreferrer">
                              Join Meeting
                            </a>
                          </small>
                        )}
                      </div>
                    ))
                  ) : (
                    <p>No events scheduled for this day.</p>
                  )}
                </div>
              </div>
            )}                                                                                        */}
          </main>
          {showMsModal && (
            <div className="Modal-backdrop">
              <div className="Modal">
                <h3>Connect your Microsoft account</h3>
                <p>Choose the type of access you're granting:</p>
                <button
                  onClick={() => {
                    window.location.href = `${API_BASE}/auth/ms/login`;
                  }}
                >
                  I’m a User
                </button>

                <button
                  onClick={() => {
                    window.location.href = `${API_BASE}/auth/ms/bot-login`;
                  }}
                >
                  I’m a Polling Bot
                </button>

                <button
                  onClick={async () => {
                    try {
                      setShowMsModal(false);

                      const scope = encodeURIComponent(
                        "offline_access User.Read Chat.ReadWrite.All ChannelMessage.Read.All Group.Read.All Team.ReadBasic.All TeamMember.Read.All Mail.Read Mail.ReadBasic MailboxFolder.Read MailboxItem.Read Calendars.ReadWrite.Shared Files.Read.All Tasks.Read OnlineMeetingTranscript.Read.All OnlineMeetingRecording.Read.All"
                      );

                      // Use access token from props
                      const statePayload = { token };
                      const encodedState = btoa(JSON.stringify(statePayload));

                      const graphAdminConsentUrl =
                        `https://login.microsoftonline.com/organizations/v2.0/adminconsent` +
                        `?client_id=${process.env.REACT_APP_CLIENT_ID}` +
                        `&redirect_uri=${API_BASE}/auth/ms/callback` +
                        `&scope=${scope}` +
                        `&state=${encodedState}`;

                      const botAdminConsentUrl =
                        `https://login.microsoftonline.com/organizations/v2.0/adminconsent` +
                        `?client_id=${process.env.BOT_APP_ID}` +
                        `&redirect_uri=${API_BASE}/auth/ms/callback`;

                      window.open(graphAdminConsentUrl, "_blank");
                      setTimeout(() => {
                        window.open(botAdminConsentUrl, "_blank");
                      }, 1000);  // slight delay to avoid popup block
                      
                    } catch (err) {
                      console.error("Admin login error:", err);
                      alert("Failed to start Microsoft admin flow.");
                    }
                  }}
                >
                  I'm an Admin
                </button>


                <button onClick={() => setShowMsModal(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )
    </div>
  );





  // Your full app logic here (states, handlers, rendering)
}

export default AppCore;